import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, formatIDR, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, Trash2, Minus, Plus, CreditCard } from "lucide-react";

/**
 * Cart page — per-trip multi-item cart that checks out as a single Escrow order.
 */
export default function Cart() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = () =>
    api.get(`/cart/${tripId}`).then((r) => setCart(r.data));

  useEffect(() => {
    if (!user) return;
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const updateQty = async (item, delta) => {
    const newQty = Math.max(0, (item.qty || 1) + delta);
    setBusy(true);
    try {
      if (newQty === 0) {
        await api.post("/cart/remove", { trip_id: tripId, catalog_item_id: item.catalog_item_id });
      } else {
        await api.post("/cart/update", { trip_id: tripId, catalog_item_id: item.catalog_item_id, qty: newQty });
      }
      await reload();
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal memperbarui");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    setBusy(true);
    try {
      await api.post("/cart/remove", { trip_id: tripId, catalog_item_id: item.catalog_item_id });
      await reload();
      toast.success("Item dihapus dari keranjang");
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menghapus");
    } finally {
      setBusy(false);
    }
  };

  const checkout = async () => {
    if (!window.confirm("Lanjutkan ke pembayaran Escrow untuk semua item di keranjang?")) return;
    setBusy(true);
    try {
      const r = await api.post("/cart/checkout", { trip_id: tripId });
      toast.success("Order dibuat. Lanjutkan ke pembayaran.");
      navigate(`/orders/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Checkout gagal");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-zinc-500">Memuat...</div>;
  if (!cart) return <div className="text-center py-20 text-zinc-500">Keranjang tidak ditemukan</div>;

  const items = cart.items || [];
  const totals = cart.totals || {};

  return (
    <div className="space-y-6" data-testid="cart-page">
      <Link to={`/trips/${tripId}`} className="text-sm flex items-center gap-2" style={{color: "var(--app-muted)"}} data-testid="cart-back-link">
        <ArrowLeft size={14} strokeWidth={1.5} /> Kembali ke Trip
      </Link>

      <div>
        <div className="label-caps mb-2">Keranjang</div>
        <h1 className="font-display text-3xl tracking-tight" data-testid="cart-heading">
          {cart.trip?.destination_city || cart.trip?.destination_country}
        </h1>
        <div className="text-sm mt-1" style={{color: "var(--app-muted)"}}>
          oleh <span className="font-medium">{cart.trip?.jastiper_name}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {items.length === 0 ? (
            <div className="surface py-16 text-center" data-testid="cart-empty">
              <ShoppingCart size={36} strokeWidth={1.5} className="mx-auto mb-3" style={{color: "var(--app-muted)"}} />
              <p className="mb-4" style={{color: "var(--app-muted)"}}>Keranjang Anda kosong</p>
              <Link to={`/trips/${tripId}`} className="btn-primary-flat text-sm" data-testid="cart-empty-browse-btn">
                Jelajahi Katalog
              </Link>
            </div>
          ) : (
            <div className="surface divide-y" style={{borderColor: "var(--app-border)"}}>
              {items.map((it) => {
                const fee = (it.fee_value > 0)
                  ? (it.fee_type === "percent" ? it.price * it.fee_value / 100 : it.fee_value)
                  : 0;
                const linePrice = (it.price + fee) * it.qty;
                return (
                  <div key={it.catalog_item_id} className="p-4 flex gap-4 items-center" data-testid={`cart-item-${it.catalog_item_id}`}>
                    <div className="w-16 h-16 flex-shrink-0" style={{backgroundColor: "var(--app-surface-alt)"}}>
                      {it.photo_path && <img src={fileUrl(it.photo_path)} alt={it.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.name}</div>
                      <div className="text-xs font-mono mt-1" style={{color: "var(--app-muted)"}}>
                        {formatIDR(it.price + fee)} / item {fee > 0 && <span>(incl. fee)</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5" data-testid={`cart-qty-${it.catalog_item_id}`}>
                      <button
                        onClick={() => updateQty(it, -1)}
                        disabled={busy}
                        className="w-8 h-8 border flex items-center justify-center hover:bg-[var(--app-surface-alt)]"
                        style={{borderColor: "var(--app-border)"}}
                        data-testid={`cart-decrement-${it.catalog_item_id}`}
                      >
                        <Minus size={14} strokeWidth={1.5} />
                      </button>
                      <span className="w-8 text-center font-mono text-sm">{it.qty}</span>
                      <button
                        onClick={() => updateQty(it, 1)}
                        disabled={busy}
                        className="w-8 h-8 border flex items-center justify-center hover:bg-[var(--app-surface-alt)]"
                        style={{borderColor: "var(--app-border)"}}
                        data-testid={`cart-increment-${it.catalog_item_id}`}
                      >
                        <Plus size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="font-mono text-sm w-24 text-right">{formatIDR(linePrice)}</div>
                    <button
                      onClick={() => remove(it)}
                      disabled={busy}
                      className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50"
                      title="Hapus item"
                      data-testid={`cart-remove-${it.catalog_item_id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="surface p-5" data-testid="cart-summary">
            <div className="label-caps mb-3">Ringkasan</div>
            <div className="font-mono text-sm space-y-1.5">
              <Row label="Subtotal Item" value={formatIDR(totals.item_total || 0)} />
              <Row
                label={`Biaya Layanan Pembeli (${totals.buyer_platform_fee_percent || 0}%)`}
                value={`+ ${formatIDR(totals.buyer_platform_fee || 0)}`}
              />
              <div className="flex justify-between font-semibold pt-2 mt-2 border-t text-base" style={{borderColor: "var(--app-border)"}}>
                <span>Total Buyer Bayar</span>
                <span data-testid="cart-gross">{formatIDR(totals.gross_amount || 0)}</span>
              </div>

              <div className="pt-3 mt-2 border-t text-xs space-y-1" style={{color: "var(--app-muted)", borderColor: "var(--app-border)"}} data-testid="jastiper-breakdown">
                <div className="label-caps mb-1">Sisi Jastiper</div>
                <div className="flex justify-between">
                  <span>Subtotal Item</span><span>{formatIDR(totals.item_total || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>− Fee Jastiper ({totals.jastiper_platform_fee_percent || 0}%)</span>
                  <span>− {formatIDR(totals.jastiper_platform_fee || 0)}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold pt-1" style={{color: "var(--app-brand)"}}>
                  <span>Jastiper Terima</span>
                  <span>{formatIDR(totals.net_to_jastiper || 0)}</span>
                </div>
              </div>
            </div>
            <button
              onClick={checkout}
              disabled={busy || items.length === 0}
              className="btn-primary-flat w-full mt-5"
              data-testid="cart-checkout-btn"
            >
              <CreditCard size={16} strokeWidth={1.5} /> Lanjut ke Escrow
            </button>
          </div>

          <div className="surface p-4 text-xs" style={{color: "var(--app-muted)"}}>
            🛡 Dana ditahan platform sampai Anda konfirmasi barang sampai.
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span style={{color: "var(--app-muted)"}}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
